import { useEffect, useMemo } from 'react';

import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';
import { removeLayerAndSource } from '@App/components/MaplibreMap/maplibre-map.utils';
import { useLandUse } from '@services/query.service';

import { LAND_USE_CONFIG } from '../scenarios.utils';

const SOURCE_ID = 'scenarios-land-use';
const FILL_LAYER_ID = 'scenarios-land-use-fill';

const LU_COLOR_MAP = Object.fromEntries(
  LAND_USE_CONFIG.categories.map((c) => [c.code, c.color]),
);

function ScenariosLandUseLayer() {
  const { map, isLoading } = useMaplibreMap();
  const { data: landUse } = useLandUse();

  const coloredFeatures = useMemo(() => {
    if (!landUse?.features) return null;

    return {
      type: 'FeatureCollection',
      features: landUse.features.map((f) => {
        const abbr = f.properties?.LU_ABBR;
        return {
          ...f,
          properties: {
            ...f.properties,
            fillColor: LU_COLOR_MAP[abbr] || '#CCCCCC',
          },
        };
      }),
    };
  }, [landUse]);

  useEffect(() => {
    if (!map || isLoading) return undefined;

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': ['get', 'fillColor'],
        'fill-opacity': 0.6,
      },
    }, 'z-index-3');

    return () => {
      removeLayerAndSource(map, FILL_LAYER_ID, SOURCE_ID);
    };
  }, [isLoading, map]);

  useEffect(() => {
    if (isLoading || !coloredFeatures || !map?.getSource(SOURCE_ID)) return;
    map.getSource(SOURCE_ID).setData(coloredFeatures);
  }, [isLoading, coloredFeatures, map]);

  return null;
}

export default ScenariosLandUseLayer;
